import Hls from "hls.js";
import {
  hlsConfigForLiveStream,
  resolveStreamPlaybackUrl,
} from "./live-stream.ts";
import { startResilientWhepPlayback } from "./whep.ts";

export type LiveStreamTransport = "hls" | "webrtc";

const HLS_MEDIA_RECOVER_ATTEMPTS = 3;
const HLS_NETWORK_RECOVER_ATTEMPTS = 4;

export interface LivePlaybackAttachOptions {
  playlistUrl: string;
  streamType: LiveStreamTransport;
  video: HTMLVideoElement;
  signal?: AbortSignal;
  onFatalError: () => void;
  onRecovering?: () => void;
}

export interface LivePlaybackSession {
  destroy: () => void;
}

/** Debounced buffering callbacks for `<video>` waiting/playing events. */
export function createVideoBufferingHandlers(
  onBufferingChange: ((busy: boolean) => void) | undefined,
  debounceMs = 320,
): {
  attach: (video: HTMLVideoElement) => void;
  detach: (video: HTMLVideoElement) => void;
  clear: () => void;
} {
  let bufferingTimer: ReturnType<typeof setTimeout> | null = null;
  let bufferingShown = false;

  const setBuffering = (busy: boolean) => {
    if (!onBufferingChange) return;
    if (busy) {
      if (bufferingTimer || bufferingShown) return;
      bufferingTimer = setTimeout(() => {
        bufferingTimer = null;
        bufferingShown = true;
        onBufferingChange(true);
      }, debounceMs);
    } else {
      if (bufferingTimer) {
        clearTimeout(bufferingTimer);
        bufferingTimer = null;
      }
      if (bufferingShown) {
        bufferingShown = false;
        onBufferingChange(false);
      }
    }
  };

  const onWaiting = () => setBuffering(true);
  const onPlaying = () => setBuffering(false);
  const onCanPlay = () => setBuffering(false);

  return {
    attach(video) {
      video.addEventListener("waiting", onWaiting);
      video.addEventListener("playing", onPlaying);
      video.addEventListener("canplay", onCanPlay);
    },
    detach(video) {
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("canplay", onCanPlay);
    },
    clear() {
      if (bufferingTimer) {
        clearTimeout(bufferingTimer);
        bufferingTimer = null;
      }
      bufferingShown = false;
    },
  };
}

function resetVideoElement(video: HTMLVideoElement): void {
  try {
    video.pause();
  } catch {
    /* ignore */
  }
  video.removeAttribute("src");
  video.srcObject = null;
  try {
    video.load();
  } catch {
    /* ignore */
  }
}

/**
 * Attach live HLS or WHEP/WebRTC to a video element with in-player recovery
 * before surfacing a fatal error to the parent.
 */
export function attachLivePlayback(
  options: LivePlaybackAttachOptions,
): LivePlaybackSession | null {
  const src = resolveStreamPlaybackUrl(options.playlistUrl);
  if (!src) {
    options.onFatalError();
    return null;
  }

  if (options.streamType === "webrtc") {
    return attachWhepPlayback({ ...options, src });
  }

  if (Hls.isSupported() && /\.m3u8(\?|$)/i.test(src)) {
    return attachHlsPlayback({ ...options, src });
  }

  if (options.video.canPlayType("application/vnd.apple.mpegurl")) {
    return attachNativeHlsPlayback({ ...options, src });
  }

  options.onFatalError();
  return null;
}

function attachHlsPlayback(
  options: LivePlaybackAttachOptions & { src: string },
): LivePlaybackSession {
  const { video, src, signal, onFatalError, onRecovering } = options;
  let destroyed = false;
  let mediaRecoverAttempts = 0;
  let networkRecoverAttempts = 0;

  const hls = new Hls(hlsConfigForLiveStream());
  hls.attachMedia(video);
  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    if (!destroyed) hls.loadSource(src);
  });
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    void video.play().catch(() => undefined);
  });
  hls.on(Hls.Events.ERROR, (_event, data) => {
    if (!data.fatal || destroyed) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
      networkRecoverAttempts += 1;
      if (networkRecoverAttempts <= HLS_NETWORK_RECOVER_ATTEMPTS) {
        onRecovering?.();
        hls.startLoad();
        return;
      }
    }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
      mediaRecoverAttempts += 1;
      if (mediaRecoverAttempts <= HLS_MEDIA_RECOVER_ATTEMPTS) {
        onRecovering?.();
        hls.recoverMediaError();
        return;
      }
    }
    onFatalError();
  });

  const onAbort = () => destroy();
  signal?.addEventListener("abort", onAbort, { once: true });

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    signal?.removeEventListener("abort", onAbort);
    hls.destroy();
    resetVideoElement(video);
  };

  return { destroy };
}

function attachNativeHlsPlayback(
  options: LivePlaybackAttachOptions & { src: string },
): LivePlaybackSession {
  const { video, src, onFatalError } = options;
  let destroyed = false;
  let nativeRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let nativeRetries = 0;
  const NATIVE_MAX_RETRIES = 4;

  const tryReload = () => {
    if (destroyed || nativeRetries >= NATIVE_MAX_RETRIES) {
      onFatalError();
      return;
    }
    nativeRetries += 1;
    video.src = src;
    void video.play().catch(() => undefined);
  };

  const onVidErr = () => {
    if (destroyed) return;
    if (nativeRetryTimer) clearTimeout(nativeRetryTimer);
    nativeRetryTimer = setTimeout(tryReload, 400 + nativeRetries * 300);
  };

  video.src = src;
  video.addEventListener("error", onVidErr);
  void video.play().catch(() => undefined);

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    if (nativeRetryTimer) clearTimeout(nativeRetryTimer);
    video.removeEventListener("error", onVidErr);
    resetVideoElement(video);
  };

  return { destroy };
}

function attachWhepPlayback(
  options: LivePlaybackAttachOptions & { src: string },
): LivePlaybackSession {
  const { video, src, signal, onFatalError, onRecovering } = options;
  let destroyed = false;
  let closeWhep: (() => void) | null = null;

  const boot = () => {
    if (destroyed) return;
    void startResilientWhepPlayback({
      url: src,
      videoEl: video,
      signal,
      onReconnecting: () => {
        if (!destroyed) onRecovering?.();
      },
      onGiveUp: () => {
        if (!destroyed) onFatalError();
      },
    })
      .then((session) => {
        if (destroyed) {
          session.close();
          return;
        }
        closeWhep = session.close;
        void video.play().catch(() => undefined);
      })
      .catch(() => {
        if (!destroyed) onFatalError();
      });
  };

  boot();

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    closeWhep?.();
    closeWhep = null;
    resetVideoElement(video);
  };

  return { destroy };
}
